import React from 'react';
import LazyImage from './LazyImage';

export interface IProps {
    /** Displays images as carousel if set to true */
    isCarousel?: boolean;
    /** An array of image objects or strings pointing to source */
    images: Array<string> | Array<{ src: string, alt: string }> | any;
    /** Controls whether to use lazy loading to load images */
    useLazy?: boolean;
    /** Allows selection of images */
    allowSelect?: boolean;
    /** Custom select handler if allowSelect=true */
    handleSelect?: any;
}

const ImagesDisplay: React.FC<IProps> = (props: IProps) => {
    const { images, isCarousel, useLazy, allowSelect, handleSelect } = props;
    const [previewImageOpen, setPreviewImageOpen] = React.useState(false);
    const indexRef: any = React.useRef(0 as number);
    const [imageIndex, setImageIndex] = React.useState(0 as number);
    const [selected, setSelected] = React.useState([] as any);

    const toggleFullImagePreview = (e: React.SyntheticEvent) => {
        e.preventDefault();
        setPreviewImageOpen(!previewImageOpen);
    }

    const handleGalleryClick = (e: React.SyntheticEvent, index: number) => {
        e.preventDefault();
        setImageIndex(index);
        setTimeout(() => {
            setPreviewImageOpen(true);
        }, 250)
    }

    const switchImage = (e: React.SyntheticEvent | null, goForward: boolean = true) => {
        e?.preventDefault();
        e?.stopPropagation();
        let newIndex: number = indexRef.current || imageIndex;
        if (goForward) {
            if (newIndex + 1 >= images.length) {
                newIndex = 0;
            } else { newIndex += 1 }
        } else {
            if (newIndex - 1 < 0) {
                newIndex = images.length - 1;
            } else { newIndex -= 1 }
        }
        setImageIndex(newIndex);
    }

    const handleImageSelect = (e: React.MouseEvent, source: string) => {
        e.preventDefault();
        let newSelected: any = [...selected];
        let foundIndex: number = newSelected.findIndex((imageSrc: string) => imageSrc === source);
        if (foundIndex >= 0) {
            newSelected.splice(foundIndex, 1);
            setSelected(newSelected);
            handleSelect && handleSelect(newSelected)
        } else {
            newSelected.push(source);
            setSelected(newSelected);
            handleSelect && handleSelect(newSelected);
        }
        //console.log(source, newSelected);
    }

    React.useEffect(() => { indexRef.current = imageIndex }, [imageIndex])

    React.useEffect(() => {
        let arrowEvents: any = document.addEventListener("keyup", (e: KeyboardEvent) => {
            const { key } = e;
            if (key === "ArrowLeft") {
                switchImage(null, false);
            }
            if (key === "ArrowRight") {
                switchImage(null, true);
            }
            if (key === "Escape") {
                setPreviewImageOpen(false);
            }
        })
        return () => {
            document.removeEventListener("keydown", arrowEvents);
        }
        //r-18-lint-dj
        //eslint-disable-next-line
    }, [])

    if (isCarousel) { //? Carousel
        return (
            <>
                <div className="d-flex justify-content-between flex-row align-items-center">
                    <i onClick={(e: React.SyntheticEvent) => switchImage(e, false)} className="material-icons cursor-on-hover image-display-carousel-ctrl">keyboard_arrow_left</i>
                    <div className="w-80">
                        {allowSelect &&
                            <i style={{ fontSize: 30 }} onClick={(e: React.MouseEvent) => handleImageSelect(e, typeof images[imageIndex] === "string" ? images[imageIndex] : images[imageIndex]?.src)} className="material-icons position-absolute top-0 end-0">
                                {selected.findIndex((s: string) => s === (typeof images[imageIndex] === "string" ? images[imageIndex] : images[imageIndex]?.src)) >= 0 ? "check_box" : "check_box_outline_blank"}
                            </i>}
                        <img
                            className="image-display-img"
                            onClick={toggleFullImagePreview}
                            alt=""
                            src={typeof images[imageIndex] === "string" ? images[imageIndex] : images[imageIndex]?.src}
                        />
                    </div>
                    <i onClick={(e: React.SyntheticEvent) => switchImage(e, true)} className="material-icons cursor-on-hover image-display-carousel-ctrl">keyboard_arrow_right</i>
                </div>
                {previewImageOpen &&
                    <div onClick={toggleFullImagePreview} className="image-display-full-image-preview">
                        <i onClick={e => switchImage(e, false)} className="material-icons image-display-full-preview-ctrl">keyboard_arrow_left</i>
                        <div className="d-flex flex-column">
                            <i onClick={toggleFullImagePreview} className="material-icons cursor-on-hover ml-auto action-text">close</i>
                            {
                                useLazy
                                    ?
                                    <LazyImage
                                        key={imageIndex}
                                        alt={typeof images[imageIndex] === "string" ? "" : images[imageIndex]?.alt || ""}
                                        onClick={(e: React.SyntheticEvent) => { e.preventDefault(); e.stopPropagation(); }}
                                        src={typeof images[imageIndex] === "string" ? images[imageIndex] : images[imageIndex]?.src || ""}
                                    />
                                    : <img
                                        key={imageIndex}
                                        alt={typeof images[imageIndex] === "string" ? "" : images[imageIndex]?.alt || ""}
                                        onClick={(e: React.SyntheticEvent) => { e.preventDefault(); e.stopPropagation(); }}
                                        src={typeof images[imageIndex] === "string" ? images[imageIndex] : images[imageIndex]?.src || ""}
                                    />
                            }
                        </div>
                        <i onClick={e => switchImage(e, true)} className="material-icons image-display-full-preview-ctrl">keyboard_arrow_right</i>
                    </div>}
            </>
        )
    } else { //? Gallery
        return (
            <div className="d-flex flex-row flex-wrap justify-content-around align-items-center">
                {
                    images.map((image: any, index: number) => {
                        const isString = typeof image === "string";
                        const source: string = isString ? image : image.src
                        return useLazy
                            ? <div className="col-6 col-md-3">
                                {allowSelect &&
                                    <i onClick={(e: React.MouseEvent) => handleImageSelect(e, source)} style={{ fontSize: 30 }} className="material-icons position-absolute top-0 end-0 action-text">
                                        {selected.findIndex((s: string) => s === source) >= 0 ? "check_box" : "check_box_outline_blank"}
                                    </i>}
                                <LazyImage className="image-display-img" key={index} onClick={(e: React.SyntheticEvent) => handleGalleryClick(e, index)} src={source} />
                            </div>
                            : <div className="col-6 col-md-3">
                                {allowSelect &&
                                    <i onClick={(e: React.MouseEvent) => handleImageSelect(e, source)} style={{ fontSize: 30 }} className="material-icons position-absolute top-0 end-0">
                                        {selected.findIndex((s: string) => s === source) >= 0 ? "check_box" : "check_box_outline_blank"}
                                    </i>}
                                <img
                                    className="image-display-img"
                                    key={index}
                                    onClick={(e: React.SyntheticEvent) => handleGalleryClick(e, index)}
                                    src={source}
                                    alt=""
                                />;
                            </div>
                    })
                }
                {previewImageOpen &&
                    <div onClick={toggleFullImagePreview} className="image-display-full-image-preview">
                        <i onClick={e => switchImage(e, false)} className="material-icons image-display-full-preview-ctrl">keyboard_arrow_left</i>
                        <div className="d-flex flex-column align-items-center">
                            <i onClick={toggleFullImagePreview} className="material-icons cursor-on-hover ml-auto image-display-full-preview-ctrl">close</i>
                            {
                                useLazy
                                    ?
                                    <LazyImage
                                        key={imageIndex}
                                        alt={typeof images[imageIndex] === "string" ? "" : images[imageIndex]?.alt || ""}
                                        onClick={(e: React.SyntheticEvent) => { e.preventDefault(); e.stopPropagation(); }}
                                        src={typeof images[imageIndex] === "string" ? images[imageIndex] : images[imageIndex]?.src || ""}
                                    />
                                    : <img
                                        key={imageIndex}
                                        alt={typeof images[imageIndex] === "string" ? "" : images[imageIndex]?.alt || ""}
                                        onClick={(e: React.SyntheticEvent) => { e.preventDefault(); e.stopPropagation(); }}
                                        src={typeof images[imageIndex] === "string" ? images[imageIndex] : images[imageIndex]?.src || ""}
                                    />
                            }
                        </div>
                        <i onClick={e => switchImage(e, true)} className="material-icons image-display-full-preview-ctrl">keyboard_arrow_right</i>
                    </div>}
            </div>
        )
    }
}

ImagesDisplay.defaultProps = {
    isCarousel: false,
    images: [],
    useLazy: true,
    allowSelect: false,
    handleSelect: null
}

export default ImagesDisplay;